import { useCallback, useEffect, useRef, useState } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  CircularProgress,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import {
  CustomSettings,
  isDefaultBusinessTemplate,
  withDefaultProformaHeaderDetails,
  withDesignVariantInSettings,
} from "subModule/src/services/invoice/types";
import dayjs from "dayjs";
import { useUserLanguages } from "components/InvoiceTemplate/hooks/useUserLanguages";
import { useDefaultLabels } from "components/InvoiceTemplate/hooks/useDefaultLabels";
import { themes } from "theme/themes";
import { useSelector } from "react-redux";
import { brandingInfo } from "features/server/brandingSlice";
import { getBrandingAboutHref } from "Helpers/brandingFooter";
import { useNavigate, useParams } from "react-router-dom";
import {
  useGetInvoiceByIDService,
  useGetInvoiceTemplateByIDService,
} from "subModule/src/services/invoice/useInvoices";
import { useSnackbar } from "notistack";
import AlertMessages from "common/Alert/AlertMessages";
import Loader from "layout/Loader";
import { usePreFillLabels } from "components/InvoiceTemplate/hooks/usePreFillLabels";
import ThermalInvoiceContent from "components/InvoiceTemplate/thermal/ThermalInvoiceContent";
import { mapInvoiceToThermalData } from "components/InvoiceTemplate/thermal/mapInvoiceToThermalData";
import ModernInvoicePreview from "components/InvoiceTemplate/ModernInvoicePreview";
import { mapInvoiceToModernData } from "components/InvoiceTemplate/mapInvoiceToModernData";
import {
  getThermalPaperWidthMm,
  isThermalPrint,
} from "components/InvoiceTemplate/thermal/thermalInvoiceUtils";
import { selectUserInfo } from "features/user/userSlice";
import { axiosClient } from "subModule/src/core/axios";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { CommonProfileService } from "subModule/src/core/services";

const defaultSettings: CustomSettings = {
  languageDisplay: "both",
  dateFormat: "DD-MM-YYYY",
  showLogo: true,
  showHeader: true,
  showVehicleInfo: true,
  showTable: true,
  showTerms: true,
  showNotes: true,
  logoPosition: "left",
  logoWidth: 150,
  logoHeight: 80,
  useDefaultLogoSize: true,
  showQR: false,
  showCompanySection: true,
  showCustomerSection: true,
  invoiceHeadingPrimary: "INVOICE",
  invoiceHeadingSecondary: "",
  showInvoiceDirection: "column",
  companyFields: {
    name: true,
    phone: true,
    email: true,
    address: true,
    vat: false,
  },
  customerFields: {
    name: true,
    phone: true,
    email: false,
    address: false,
    vat: false,
  },
  vehicleFields: {
    color: false,
    fuelType: true,
    vin: false,
    mileage: true,
    make: false,
    model: false,
    year: false,
    plateNumber: false,
  },
  tableLayout: "combined",
  showPackages: true,
  showServices: true,
  showItems: true,
  descriptionDisplay: "all",
  tableColumns: {
    serialNo: true,
    description: true,
    quantity: true,
    unitPrice: true,
    discount: true,
    tax: true,
    amount: true,
  },
  consolidatedTableColumns: {
    serialNo: true,
    vehicleName: true,
    vehiclePlateNo: true,
    deliveryNoteNo: true,
    subtotal: true,
    discount: true,
    tax: true,
    total: true,
  },
  summaryItems: {
    subtotal: true,
    discount: true,
    discountedSubtotal: true,
    tax: true,
    total: true,
  },
  showPaymentInfo: true,
  printFormat: "normal",
  thermalPaperWidthPreset: "80",
  thermalCustomWidthMm: 80,
  showFooter: true,
  footerText: "This is system generated invoice.",
  footerItems: {
    address: true,
    email: true,
    phone: true,
    socialLinks: false,
    pageNumber: true,
  },
  sectionOrder: [
    "logo",
    "header",
    "vehicleInfo",
    "table",
    "summary",
    "terms",
    "notes",
    "footer",
  ],
};
const InvoiceDownloadPreview = () => {
  const { t, i18n } = useTranslation();
  const params = useParams();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { primaryLanguage, secondaryLanguage } = useUserLanguages();
  const { preFillLabels } = usePreFillLabels();
  const loggedInUserCurrency = useSelector(selectUserInfo)?.business?.currency;
  const branding = useSelector(brandingInfo);
  const themeColors =
    branding?.attributes?.themeId === 100
      ? branding?.attributes?.customTheme
      : (themes as any)[branding?.attributes?.themeId || 3];
  const [settings, setSettings] = useState<any>({});
  const [invoice, setInvoice] = useState<any>({});
  const [downloading, setDownloading] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [stationLogoBase64Src, setStationLogoBase64Src] = useState<string>("");
  const invoiceRef = useRef<HTMLDivElement>(null);
  const { isRefetching: invoiceRefetch, refetch: invoiceByIdRefetch } =
    useGetInvoiceByIDService(Number(params?.id));
  const { isRefetching, refetch: templateByIdRefetch } =
    useGetInvoiceTemplateByIDService(Number(params?.templateId));
  const getTemplateById = useCallback(() => {
    templateByIdRefetch().then((res: any) => {
      if (res?.data?.success) {
        const template = res?.data?.data || {};
        const templateSettings = withDesignVariantInSettings(
          template.attributes as CustomSettings,
          template.designVariant,
        );
        const enrichedSettings = preFillLabels(
          withDefaultProformaHeaderDetails(
            templateSettings || defaultSettings,
            template.type,
            isDefaultBusinessTemplate(template),
          ),
        );
        setSettings(enrichedSettings);
      } else
        enqueueSnackbar(
          <AlertMessages text={res?.data?.message || "Something went wrong"} />,
          { variant: "error" },
        );
    });
  }, []);
  const getInvoiceById = useCallback(() => {
    invoiceByIdRefetch()
      .then((res: any) => {
        if (res?.data?.success) {
          setInvoice(res?.data?.data || {});
        } else
          enqueueSnackbar(
            <AlertMessages
              text={res?.data?.message || "Something went wrong"}
            />,
            { variant: "error" },
          );
      })
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => {
    getTemplateById();
    getInvoiceById();
  }, []);
  useEffect(() => {
    const stationImgUrl = invoice?.attributes?.stationImgUrl
      ? invoice.attributes.stationImgUrl.split("?")[0].split("/").pop() ||
        invoice.attributes.stationImgUrl
      : undefined;
    let isActive = true;
    const getMimeTypeFromFilename = (filename: string) => {
      const clean = filename.split("?")[0].toLowerCase();
      if (clean.endsWith(".png")) return "image/png";
      if (clean.endsWith(".jpg") || clean.endsWith(".jpeg"))
        return "image/jpeg";
      if (clean.endsWith(".webp")) return "image/webp";
      if (clean.endsWith(".gif")) return "image/gif";
      if (clean.endsWith(".bmp")) return "image/bmp";
      if (clean.endsWith(".svg")) return "image/svg+xml";
      return "image/png";
    };
    const normalizeToDataUrl = (
      base64OrDataUrl: string,
      filenameForMime: string,
    ) => {
      const trimmed = (base64OrDataUrl || "").trim();
      if (!trimmed) return "";
      if (trimmed.startsWith("data:")) return trimmed;
      const mime = getMimeTypeFromFilename(filenameForMime);
      return `data:${mime};base64,${trimmed}`;
    };
    const run = async () => {
      if (!stationImgUrl) {
        setStationLogoBase64Src("");
        return;
      }
      try {
        const base64 = await CommonProfileService.convertFilenameToBase64({
          imageUrl: stationImgUrl,
        });
        if (!isActive) return;
        setStationLogoBase64Src(normalizeToDataUrl(base64, stationImgUrl));
      } catch (e) {
        if (!isActive) return;
        setStationLogoBase64Src("");
      }
    };
    run();
    return () => {
      isActive = false;
    };
  }, [invoice?.attributes?.stationImgUrl]);
  const {
    getCompanyLabel,
    getCustomerLabel,
    getVehicleLabel,
    getTableColumnLabel,
    getConsolidatedTableColumnLabel,
    getSummaryLabel,
    getPaymentLabel,
    getTableSectionLabel,
    // getTermsLabel,
    // getFooterLabel,
  } = useDefaultLabels();
  const languageCodeMap: Record<string, string> = {
    english: "en",
    arabic: "ar",
    urdu: "ur",
    french: "fr",
    en: "en",
    ar: "ar",
    ur: "ur",
    fr: "fr",
  };
  const formatNumber = (num: any) => {
    return Intl.NumberFormat("en", {
      style: "decimal",
      maximumFractionDigits: 2,
      minimumFractionDigits: 2,
    }).format(Number(num || 0));
  };
  const getTranslation = (
    key: string,
    lang: "primary" | "secondary",
  ): string => {
    const languageCode =
      lang === "primary"
        ? languageCodeMap[primaryLanguage] || "en"
        : languageCodeMap[secondaryLanguage] || "ar";

    try {
      // Try to get translation directly from i18n resources first (more reliable for Arabic)
      const resources = i18n.getResourceBundle(languageCode, "translation");
      if (resources && resources[key]) {
        const translation = resources[key];
        // Only return if it's a valid translation (not the key itself)
        if (
          translation &&
          translation !== key &&
          typeof translation === "string"
        ) {
          return translation;
        }
      }

      // Fallback to getFixedT - this is what normal table columns use
      const tForLang = i18n.getFixedT(languageCode, "translation");
      const translation = tForLang(key);

      // Return translation if it exists and is different from the key
      if (
        translation &&
        translation !== key &&
        typeof translation === "string"
      ) {
        return translation;
      }

      // If translation not found, return the key (fallbacks will handle it)
      return key;
    } catch (error) {
      // If all else fails, return the key (fallbacks will handle it)
      return key;
    }
  };

  const renderLanguage = (primary: string, secondary: string) => {
    const languageDisplay = settings.languageDisplay || "both";
    const isSecondaryArabicOrUrdu = ["ar", "ur"].includes(
      secondaryLanguage.toLowerCase(),
    );

    if (languageDisplay === "both") {
      return (
        <span>
          {primary}{" "}
          <span className={`${isSecondaryArabicOrUrdu ? "ff-arabic" : ""}`}>
            {secondary}
          </span>
        </span>
      );
    } else if (languageDisplay === "secondary") {
      return (
        <span className={isSecondaryArabicOrUrdu ? "ff-arabic" : ""}>
          {secondary}
        </span>
      );
    }
    return <span>{primary}</span>;
  };

  const formatDate = (date: string | undefined) => {
    if (!date) return "";
    const format = settings.dateFormat || "DD-MM-YYYY";
    try {
      return dayjs(date).format(format);
    } catch (error) {
      // Fallback to default format if custom format is invalid
      return dayjs(date).format("DD-MM-YYYY");
    }
  };

  const getLabel = (
    field: string,
    type: "company" | "customer" | "vehicle" = "company",
  ) => {
    const customLabels =
      type === "company"
        ? settings.companyLabels
        : type === "customer"
          ? settings.customerLabels
          : settings.vehicleLabels;

    // Get default labels from translation files
    const defaultLabels =
      type === "company"
        ? getCompanyLabel(field)
        : type === "customer"
          ? getCustomerLabel(field)
          : getVehicleLabel(field);

    // Use custom labels if they exist and are not empty, otherwise use defaults
    return {
      primary:
        customLabels?.[field]?.primary &&
        customLabels[field].primary.trim() !== ""
          ? customLabels[field].primary
          : defaultLabels.primary,
      secondary:
        customLabels?.[field]?.secondary &&
        customLabels[field].secondary.trim() !== ""
          ? customLabels[field].secondary
          : defaultLabels.secondary,
    };
  };
  const formatCurrency = (value: number | undefined) => {
    if (!value) return "0.00";
    return value.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  };
  const renderLogoSection = () => {
    const logoUrl =
      stationLogoBase64Src ||
      invoice.attributes?.stationImgUrl ||
      "/images/logo.png";

    const invoiceNo = invoice.invoiceNo
      ? invoice.invoiceNo
      : invoice.deliveryNoteNo
        ? invoice.deliveryNoteNo
        : "";

    const getInvoiceLabel = (no: string) => {
      if (!no) return "INVOICE";

      if (no.startsWith("EST")) return "ESTIMATION";
      if (no.startsWith("JC")) return "JOB CARD";
      if (no.startsWith("WO")) return "WORK ORDER";
      if (no.startsWith("DN")) return "DELIVERY NOTE";

      return "INVOICE";
    };

    const invoiceLabel = getInvoiceLabel(invoiceNo);
    const date = formatDate(invoice.createdAt);
    const qrCode = invoice.qr;

    // Determine positions
    const logoPosition = settings.logoPosition || "left";
    const invoiceNumberPosition = settings.invoiceNumberPosition || "center";
    const qrPosition = settings.qrPosition || "right";

    // Check if each element should be shown
    const showLogo = settings.showLogo !== false && logoPosition !== "none";
    const showInvoiceNumber =
      settings.showInvoiceNumber !== false && invoiceNumberPosition !== "none";
    const showQR = settings.showQR !== false && qrPosition !== "none" && qrCode;

    // If nothing to show, return null
    if (!showLogo && !showInvoiceNumber && !showQR) return null;

    // Group elements by their positions
    const leftElements = [];
    const centerElements = [];
    const rightElements = [];

    // Logo
    if (showLogo && logoPosition !== "none") {
      const logoElement = (
        <div
          key="logo"
          style={{
            display: "flex",
            justifyContent: "flex-start",
            alignItems: "center",
          }}
        >
          <img
            // crossOrigin="anonymous"
            src={logoUrl}
            alt="Logo"
            style={{
              width:
                settings.useDefaultLogoSize === false &&
                settings.logoWidth &&
                settings.logoHeight
                  ? settings.logoWidth
                  : 150,
              // height:
              //     settings.useDefaultLogoSize === false &&
              //         settings.logoWidth &&
              //         settings.logoHeight
              //         ? settings.logoHeight
              //         : 80,
              maxWidth: "100%",
              // objectFit: 'contain',
            }}
          />
        </div>
      );

      if (logoPosition === "left") leftElements.push(logoElement);
      else if (logoPosition === "center") centerElements.push(logoElement);
      else if (logoPosition === "right") rightElements.push(logoElement);
    }

    // Invoice Number
    if (
      showInvoiceNumber &&
      invoiceNumberPosition !== "none" &&
      settings.showInvoiceDirection !== "row"
    ) {
      const invoiceElement = (
        <div
          key="invoice"
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            flexDirection: "column",
          }}
        >
          <Typography
            style={{
              fontSize: settings.invoiceNumberFontSize || 14,
              fontWeight: settings.invoiceNumberFontWeight || 700,
              color: themeColors?.secondary,
            }}
          >
            {renderLanguage(
              invoiceLabel,
              settings.invoiceHeadingSecondary || "",
            )}{" "}
            # : {invoiceNo}
          </Typography>
          {/* <br /> */}
          <Typography className="text-[13px] text-center w-[100%]">
            {t("date")}: {date}
          </Typography>
        </div>
      );

      if (invoiceNumberPosition === "left") leftElements.push(invoiceElement);
      else if (invoiceNumberPosition === "center")
        centerElements.push(invoiceElement);
      else if (invoiceNumberPosition === "right")
        rightElements.push(invoiceElement);
    }

    // QR Code
    if (
      showQR &&
      qrPosition !== "none" &&
      settings.showInvoiceDirection !== "row"
    ) {
      const qrElement = (
        <div
          key="qr"
          style={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            marginBottom: 16,
          }}
        >
          <div
            style={{
              width: 100,
              height: 100,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <img
              src={`data:image/png;base64,${qrCode}`}
              alt="QR Code"
              style={{
                width: 100,
                height: 100,
                maxWidth: "100%",
                maxHeight: "100%",
              }}
            />
          </div>
        </div>
      );

      if (qrPosition === "left") leftElements.push(qrElement);
      else if (qrPosition === "center") centerElements.push(qrElement);
      else if (qrPosition === "right") rightElements.push(qrElement);
    }

    // Render columns with stacked elements
    return (
      <div
        className="invoice-section"
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 24,
          gap: 16,
        }}
      >
        {/* Left Column */}
        <div
          style={{
            flex: 1,
            minWidth: 0,
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
          }}
        >
          {leftElements}
        </div>

        {/* Center Column */}
        <div
          style={{
            flex: 1,
            minWidth: 0,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          {centerElements}
        </div>

        {/* Right Column */}
        <div
          style={{
            flex: 1,
            minWidth: 0,
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-end",
          }}
        >
          {rightElements}
        </div>
      </div>
    );
  };

  const renderInvoiceNumberRow = () => {
    // Only render if showInvoiceDirection is 'row' and showInvoiceNumber is not false
    const showInvoiceInRow = settings.showInvoiceDirection === "row";
    const showInvoiceNumber = settings.showInvoiceNumber !== false;
    const numberPosition = settings.invoiceNumberPosition || "left";

    if (!showInvoiceInRow || !showInvoiceNumber) return null;
    const invoiceNo = invoice.invoiceNo
      ? invoice.invoiceNo
      : invoice.deliveryNoteNo
        ? invoice.deliveryNoteNo
        : "";
    const date = formatDate(invoice.createdAt);
    const qrCode = invoice.qr;
    const showQR = settings.showQR === true && qrCode;

    return (
      <div
        className="invoice-number-row"
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 24,
          padding: "12px 16px",
          borderRadius: 8,
        }}
      >
        {/* Spacer for center/right alignment */}
        {numberPosition === "center" || numberPosition === "right" ? (
          <div style={{ flex: 1 }} />
        ) : null}

        {/* Invoice Number Section */}
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 4,
            alignItems:
              numberPosition === "left"
                ? "flex-start"
                : numberPosition === "center"
                  ? "center"
                  : "flex-end",
            flex: numberPosition === "center" ? 1 : "none",
          }}
        >
          <Typography
            style={{
              fontSize: settings.invoiceNumberFontSize || 18,
              fontWeight: settings.invoiceNumberFontWeight || 700,
              color: themeColors.secondary,
            }}
          >
            {renderLanguage(
              settings.invoiceHeadingPrimary || "INVOICE",
              settings.invoiceHeadingSecondary || "",
            )}{" "}
            # : {invoiceNo}
          </Typography>
          <Typography style={{ fontSize: 13 }}>
            {t("date")}: {date}
          </Typography>
        </div>

        {/* Spacer for left/center alignment */}
        {numberPosition === "left" || numberPosition === "center" ? (
          <div style={{ flex: 1 }} />
        ) : null}

        {/* QR Code Section (if enabled) - Always at the end */}
        {showQR && (
          <div
            style={{
              width: 100,
              height: 100,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <img
              src={`data:image/png;base64,${qrCode}`}
              alt="QR Code"
              style={{
                width: 100,
                height: 100,
                maxWidth: "100%",
                maxHeight: "100%",
              }}
            />
          </div>
        )}
      </div>
    );
  };
  const renderConsolidatedTable = () => {
    const consolidatedColumns = settings.consolidatedTableColumns || {};
    const deliveryNotes =
      invoice.deliveryNoteResponses || invoice.arrayData || [];

    if (deliveryNotes.length === 0) return null;

    // Helper function to render currency cell
    const renderCurrencyCell = (value: number) => (
      <>
        <Box component="strong" sx={{ color: "primary.main", mx: 0.5 }}>
          {formatCurrency(value)}
        </Box>
        <span>{loggedInUserCurrency?.toUpperCase() || ""}</span>
      </>
    );

    // Helper function to render table header cell
    const renderTableHeaderCell = (
      content: React.ReactNode,
      align: "left" | "center" | "right" = "left",
    ) => (
      <TableCell
        sx={{
          border: "1px solid #ddd",
          padding: "12px",
          fontWeight: "bold",
          backgroundColor: "#f5f5f5",
          textAlign: align,
        }}
      >
        {content}
      </TableCell>
    );

    // Helper function to render table body cell
    const renderTableBodyCell = (
      content: React.ReactNode,
      align: "left" | "center" | "right" = "left",
    ) => (
      <TableCell
        sx={{
          border: "1px solid #ddd",
          padding: "8px",
          textAlign: align,
        }}
      >
        {content}
      </TableCell>
    );

    return (
      <Box sx={{ mb: 3 }}>
        <TableContainer>
          <Table
            sx={{
              border: "1px solid #ddd",
              borderCollapse: "collapse",
              fontSize: "13px",
            }}
            size="small"
          >
            <TableHead>
              <TableRow>
                {/* Serial Number Column */}
                {consolidatedColumns.serialNo !== false &&
                  renderTableHeaderCell(
                    renderLanguage(
                      getConsolidatedTableColumnLabel("serialNo").primary,
                      getConsolidatedTableColumnLabel("serialNo").secondary,
                    ),
                    "left",
                  )}

                {/* Vehicle Name Column */}
                {consolidatedColumns.vehicleName !== false &&
                  renderTableHeaderCell(
                    renderLanguage(
                      getConsolidatedTableColumnLabel("vehicleName").primary,
                      getConsolidatedTableColumnLabel("vehicleName").secondary,
                    ),
                    "left",
                  )}

                {/* Vehicle Plate Number Column */}
                {consolidatedColumns.vehiclePlateNo !== false &&
                  renderTableHeaderCell(
                    renderLanguage(
                      getConsolidatedTableColumnLabel("vehiclePlateNo").primary,
                      getConsolidatedTableColumnLabel("vehiclePlateNo")
                        .secondary,
                    ),
                    "left",
                  )}

                {/* Delivery Note Number Column */}
                {consolidatedColumns.deliveryNoteNo !== false &&
                  renderTableHeaderCell(
                    renderLanguage(
                      getConsolidatedTableColumnLabel("deliveryNoteNo").primary,
                      getConsolidatedTableColumnLabel("deliveryNoteNo")
                        .secondary,
                    ),
                    "left",
                  )}

                {/* Subtotal Column */}
                {consolidatedColumns.subtotal !== false &&
                  renderTableHeaderCell(
                    renderLanguage(
                      getConsolidatedTableColumnLabel("subtotal").primary,
                      getConsolidatedTableColumnLabel("subtotal").secondary,
                    ),
                    "right",
                  )}

                {/* Discount Column */}
                {consolidatedColumns.discount !== false &&
                  renderTableHeaderCell(
                    renderLanguage(
                      getConsolidatedTableColumnLabel("discount").primary,
                      getConsolidatedTableColumnLabel("discount").secondary,
                    ),
                    "right",
                  )}

                {/* Tax Column */}
                {consolidatedColumns.tax !== false &&
                  renderTableHeaderCell(
                    renderLanguage(
                      getConsolidatedTableColumnLabel("tax").primary,
                      getConsolidatedTableColumnLabel("tax").secondary,
                    ),
                    "right",
                  )}

                {/* Total Column */}
                {consolidatedColumns.total !== false &&
                  renderTableHeaderCell(
                    renderLanguage(
                      getConsolidatedTableColumnLabel("total").primary,
                      getConsolidatedTableColumnLabel("total").secondary,
                    ),
                    "right",
                  )}
              </TableRow>
            </TableHead>
            <TableBody>
              {deliveryNotes.map((note: any, index: number) => (
                <TableRow
                  key={note.id?.toString() || note.deliveryNoteNo || index}
                >
                  {/* Serial Number Cell */}
                  {consolidatedColumns.serialNo !== false &&
                    renderTableBodyCell(note.sortOrder || index + 1, "left")}

                  {/* Vehicle Name Cell */}
                  {consolidatedColumns.vehicleName !== false &&
                    renderTableBodyCell(note.vehicleName || "N/A", "left")}

                  {/* Vehicle Plate Number Cell */}
                  {consolidatedColumns.vehiclePlateNo !== false &&
                    renderTableBodyCell(note.vehiclePlateNo || "N/A", "left")}

                  {/* Delivery Note Number Cell */}
                  {consolidatedColumns.deliveryNoteNo !== false &&
                    renderTableBodyCell(note.deliveryNoteNo || "N/A", "left")}

                  {/* Subtotal Cell */}
                  {consolidatedColumns.subtotal !== false &&
                    renderTableBodyCell(
                      renderCurrencyCell(
                        note.subtotalAmount || note.subTotal || 0,
                      ),
                      "right",
                    )}

                  {/* Discount Cell */}
                  {consolidatedColumns.discount !== false &&
                    renderTableBodyCell(
                      renderCurrencyCell(note.discount || 0),
                      "right",
                    )}

                  {/* Tax Cell */}
                  {consolidatedColumns.tax !== false &&
                    renderTableBodyCell(
                      renderCurrencyCell(note.taxAmount || note.tax || 0),
                      "right",
                    )}

                  {/* Total Cell */}
                  {consolidatedColumns.total !== false &&
                    renderTableBodyCell(
                      renderCurrencyCell(note.totalAmount || note.total || 0),
                      "right",
                    )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    );
  };
  const renderSection = (sectionId: string) => {
    switch (sectionId) {
      case "logo": {
        return (
          <>
            {renderLogoSection()}
            {renderInvoiceNumberRow()}
          </>
        );
      }

      case "header": {
        const languageDisplay = settings.languageDisplay || "both";

        if (settings.showHeader === false) return null;

        const showCompany = settings.showCompanySection !== false;
        const showCustomer = settings.showCustomerSection !== false;

        if (!showCompany && !showCustomer) return null;

        const companyFieldsConfig = settings.companyFields || {};
        const customerFieldsConfig = settings.customerFields || {};

        const companyData: Record<string, any> = {
          name: invoice.attributes?.stationName || "",
          phone: invoice.attributes?.stationPhone || "",
          email: invoice.attributes?.stationEmail || "",
          address: invoice.attributes?.stationAddress || "",
          vat: invoice.attributes?.stationTaxNumber || "",
        };

        const customerData: Record<string, any> = {
          name:
            invoice?.attributes?.companyName ||
            invoice.attributes?.customerName ||
            "",
          phone:
            invoice?.attributes?.companyPhone ||
            invoice.attributes?.customerPhone ||
            "",
          email:
            invoice?.invoiceType === 60
              ? invoice?.attributes?.companyEmail || ""
              : !invoice?.attributes?.autoDetails
                ? invoice?.attributes?.companyEmail ||
                  invoice?.attributes?.customerEmail ||
                  ""
                : invoice?.attributes?.autoDetails === 1 ||
                    invoice?.attributes?.autoDetails === 30
                  ? invoice?.attributes?.companyEmail ||
                    invoice?.attributes?.customerEmail ||
                    ""
                  : "",
          address:
            invoice?.attributes?.companyAddress ||
            invoice.attributes?.customerAddress ||
            "",
          vat:
            invoice?.attributes?.companyTaxNumber ||
            invoice.attributes?.customerTaxNumber ||
            "",
        };

        const companyFields = showCompany
          ? Object.keys(companyFieldsConfig).filter(
              (field) => companyFieldsConfig[field] === true,
            )
          : [];

        const customerFields = Object.keys(customerFieldsConfig).filter(
          (field) => customerFieldsConfig[field] === true,
        );

        // FIX: align fields row-by-row safely
        const maxRows = Math.max(companyFields.length, customerFields.length);
        const rows = [];

        for (let i = 0; i < maxRows; i++) {
          const companyField = companyFields[i];
          const customerField = customerFields[i];

          rows.push(
            <tr key={i}>
              {/* LEFT: COMPANY */}
              {showCompany && (
                <td
                  colSpan={2}
                  style={{
                    border: "1px solid #ddd",
                    padding: "8px",
                    width: showCustomer ? "50%" : "100%",
                  }}
                >
                  {companyField ? (
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      {languageDisplay !== "secondary" && (
                        <span
                          style={{
                            fontWeight: 500,
                            textAlign: "left",
                            width: "30%",
                          }}
                        >
                          {getLabel(companyField, "company").primary}:
                        </span>
                      )}

                      <span
                        style={{
                          textAlign: "left",
                          width: "40%",
                          wordBreak: "break-all",
                        }}
                      >
                        {companyData[companyField]}
                      </span>

                      {languageDisplay !== "primary" && (
                        <span
                          style={{
                            fontWeight: 500,
                            textAlign: "right",
                            width: "30%",
                          }}
                          className={
                            ["ar", "ur"].includes(
                              secondaryLanguage.toLowerCase(),
                            )
                              ? "ff-arabic"
                              : ""
                          }
                        >
                          :{getLabel(companyField, "company").secondary}
                        </span>
                      )}
                    </div>
                  ) : null}
                </td>
              )}

              {/* RIGHT: CUSTOMER */}
              {showCustomer && (
                <td
                  colSpan={2}
                  style={{
                    border: "1px solid #ddd",
                    padding: "8px",
                    width: showCompany ? "50%" : "100%",
                  }}
                >
                  {customerField ? (
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      {languageDisplay !== "secondary" && (
                        <span
                          style={{
                            fontWeight: 500,
                            textAlign: "left",
                            width: "30%",
                          }}
                        >
                          {getLabel(customerField, "customer").primary}:
                        </span>
                      )}

                      <span
                        style={{
                          textAlign: "left",
                          width: "40%",
                          wordBreak: "break-all",
                        }}
                      >
                        {customerData[customerField] || ""}
                      </span>

                      {languageDisplay !== "primary" && (
                        <span
                          style={{
                            fontWeight: 500,
                            textAlign: "right",
                            width: "30%",
                          }}
                          className={
                            ["ar", "ur"].includes(
                              secondaryLanguage.toLowerCase(),
                            )
                              ? "ff-arabic"
                              : ""
                          }
                        >
                          :{getLabel(customerField, "customer").secondary}
                        </span>
                      )}
                    </div>
                  ) : null}
                </td>
              )}
            </tr>,
          );
        }

        return (
          <div className="invoice-section" style={{ marginBottom: 24 }}>
            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
                border: "1px solid #ddd",
              }}
            >
              <tbody className="text-[13px]">{rows}</tbody>
            </table>
          </div>
        );
      }

      case "vehicleInfo": {
        const languageDisplay = settings.languageDisplay || "both";

        // IMPORTANT: Do NOT render if invoiceType === 50 (consolidated invoices don't show vehicle info)
        if (invoice.invoiceType === 50) return null;
        if (settings.showVehicleInfo === false) return null;

        const vehicleFieldsConfig = settings.vehicleFields || {};
        const vehicleData: Record<string, any> = {
          make: invoice.attributes?.makeTitle || "",
          model: invoice.attributes?.modelTitle || "",
          year: invoice.attributes?.year || "",
          plateNumber: invoice.vehiclePlateNo,
          vin: invoice.vehicleVin,
          color: invoice.attributes?.color,
          fuelType: invoice.attributes?.fuelTypeTitle || "",
          mileage: invoice.attributes?.odometer,
        };

        const fieldsToShow = Object.keys(vehicleFieldsConfig).filter(
          (field) => vehicleFieldsConfig[field] === true,
        );

        if (fieldsToShow.length === 0) return null;

        const rows = [];
        for (let i = 0; i < fieldsToShow.length; i += 2) {
          const field1 = fieldsToShow[i];
          const field2 = fieldsToShow[i + 1];

          rows.push(
            <tr key={i}>
              <td
                style={{
                  border: "1px solid #ddd",
                  padding: "8px",
                  width: "50%",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  {languageDisplay !== "secondary" && (
                    <span style={{ fontWeight: 500 }}>
                      {getLabel(field1, "vehicle").primary}:
                    </span>
                  )}
                  <span>{vehicleData[field1]}</span>
                  {languageDisplay !== "primary" && (
                    <span
                      style={{ fontWeight: 500 }}
                      className={
                        ["ar", "ur"].includes(secondaryLanguage.toLowerCase())
                          ? "ff-arabic"
                          : ""
                      }
                    >
                      :{getLabel(field1, "vehicle").secondary}
                    </span>
                  )}
                </div>
              </td>
              {field2 ? (
                <td
                  style={{
                    border: "1px solid #ddd",
                    padding: "8px",
                    width: "50%",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    {languageDisplay !== "secondary" && (
                      <span style={{ fontWeight: 500 }}>
                        {getLabel(field2, "vehicle").primary}:
                      </span>
                    )}
                    <span>{vehicleData[field2]}</span>
                    {languageDisplay !== "primary" && (
                      <span
                        style={{ fontWeight: 500 }}
                        className={
                          ["ar", "ur"].includes(secondaryLanguage.toLowerCase())
                            ? "ff-arabic"
                            : ""
                        }
                      >
                        :{getLabel(field2, "vehicle").secondary}
                      </span>
                    )}
                  </div>
                </td>
              ) : (
                <td style={{ border: "1px solid #ddd" }}></td>
              )}
            </tr>,
          );
        }

        const vehicleHeading = `${vehicleData.make || ""} ${
          vehicleData.model || ""
        } ${vehicleData.year || ""}`.trim();
        const plateNumber = vehicleData.plateNumber || "";

        return (
          <div className="invoice-section" style={{ marginBottom: 24 }}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                marginBottom: 8,
                alignItems: "center",
              }}
            >
              <Typography
                style={{
                  color: themeColors?.primary,
                  margin: 0,
                  fontSize: "13px",
                  fontWeight: "700",
                }}
              >
                {vehicleHeading}
              </Typography>
              <Typography
                style={{
                  color: themeColors.primary,
                  margin: 0,
                  border: "1px solid black",
                  borderLeft: "10px solid " + themeColors.primary,
                  padding: "3px 5px",
                  fontSize: "13px",
                  fontWeight: "700",
                }}
              >
                {plateNumber}
              </Typography>
            </div>
            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
                border: "1px solid #ddd",
              }}
            >
              <tbody className="text-[13px]">{rows}</tbody>
            </table>
          </div>
        );
      }

      case "table": {
        // Check if table should be shown
        if (settings.showTable === false) return null;

        // Check if this is a consolidated invoice (invoiceType === 50)
        const isConsolidated = invoice.invoiceType === 50;

        // Note: consolidated table rendering would need to be implemented separately
        if (isConsolidated) {
          return renderConsolidatedTable();
        }

        const tableLayout = settings.tableLayout || "combined";
        const descriptionDisplay = settings.descriptionDisplay || "title";
        const tableColumns = settings.tableColumns || {};

        const formatDescription = (item: any) => {
          const title = item.title || item.itemName || "";
          const code =
            item.code ||
            item.itemCode ||
            item.serviceCode ||
            item.packageCode ||
            "";

          if (descriptionDisplay === "both" && code) {
            return `${title} - ${code}`;
          } else if (descriptionDisplay === "code" && code) {
            return code;
          }
          return title;
        };

        // Collect items
        const services = (invoice.invoiceServices || []).map((item: any) => ({
          ...item,
          type: "service",
          description: formatDescription(item),
          quantity: item.quantity,
          unitPrice: item.price || item.unitPrice,
          discount: item.discount || "",
          tax: item.taxAmount || item.tax || "",
          amount: item.totalAmount,
          unitTitle: "Service(s)",
        }));

        const items = (invoice.invoiceItems || []).map((item: any) => ({
          ...item,
          type: "item",
          description: formatDescription(item),
          quantity: item.quantity,
          unitPrice: item.unitPrice,
          discount: item.discount || "",
          tax: item.taxAmount || item.tax || "",
          amount: item.totalAmount,
          unitTitle: item.unitTitle || "Box",
        }));

        const packages = (invoice.invoicePackages || []).map((item: any) => ({
          ...item,
          type: "package",
          description: formatDescription(item),
          quantity: item.quantity,
          unitPrice: item.unitPrice,
          discount: item.discount || "",
          tax: item.taxAmount || item.tax || "",
          amount: item.totalAmount,
          unitTitle: "Package(s)",
        }));

        // Helper function to collect all items for separate tables
        const collectForSeparateTables = () => {
          const allPackages: any[] = [];
          const allServices: any[] = [];
          const allItems: any[] = [];

          // Collect packages (top level only)
          packages.forEach((pkg: any) => {
            allPackages.push({
              ...pkg,
              type: "package",
              description: formatDescription(pkg),
              quantity: pkg.quantity,
              unitPrice: pkg.unitPrice,
              discount: pkg.discount || "",
              tax: pkg.taxAmount || pkg.tax || "",
              amount: pkg.totalAmount,
              unitTitle: "Package(s)",
              level: 0,
            });
          });

          // Collect all services: invoiceServices + services nested in packages
          services.forEach((service: any) => {
            allServices.push({
              ...service,
              type: "service",
              description: formatDescription(service),
              quantity: service.quantity,
              unitPrice: service.price || service.unitPrice,
              discount: service.discount || "",
              tax: service.taxAmount || service.tax || "",
              amount: service.totalAmount,
              unitTitle: "Service(s)",
              level: 0,
            });
          });

          // Collect services nested in packages
          packages.forEach((pkg: any) => {
            pkg.services?.forEach((service: any) => {
              allServices.push({
                ...service,
                type: "service",
                description: formatDescription(service),
                quantity: service.quantity,
                unitPrice: service.price || service.unitPrice,
                discount: service.discount || "",
                tax: service.taxAmount || service.tax || "",
                amount: service.totalAmount,
                unitTitle: "Service(s)",
                level: 0,
              });
            });
          });

          // Collect all items: invoiceItems + package nested items + package/service nested items + service nested items
          items.forEach((item: any) => {
            allItems.push({
              ...item,
              type: "item",
              description: formatDescription(item),
              quantity: item.quantity,
              unitPrice: item.unitPrice,
              discount: item.discount || "",
              tax: item.taxAmount || item.tax || "",
              amount: item.totalAmount,
              unitTitle: item.unitTitle || "Box",
              level: 0,
            });
          });

          // Collect items nested in packages
          packages.forEach((pkg: any) => {
            pkg.items?.forEach((item: any) => {
              allItems.push({
                ...item,
                type: "item",
                description: formatDescription(item),
                quantity: item.quantity,
                unitPrice: item.price || item.unitPrice,
                discount: item.discount || "",
                tax: item.taxAmount || item.tax || "",
                amount: item.totalAmount,
                unitTitle: item.unitTitle || "Box",
                level: 0,
              });
            });
          });

          // Collect items nested in services within packages
          packages.forEach((pkg: any) => {
            pkg.services?.forEach((service: any) => {
              service.items?.forEach((item: any) => {
                allItems.push({
                  ...item,
                  type: "item",
                  description: formatDescription(item),
                  quantity: item.quantity,
                  unitPrice: item.price || item.unitPrice,
                  discount: item.discount || "",
                  tax: item.taxAmount || item.tax || "",
                  amount: item.totalAmount,
                  unitTitle: item.unitTitle || "Box",
                  level: 0,
                });
              });
            });
          });

          // Collect items nested in standalone services
          services.forEach((service: any) => {
            service.items?.forEach((item: any) => {
              allItems.push({
                ...item,
                type: "item",
                description: formatDescription(item),
                quantity: item.quantity,
                unitPrice: item.price || item.unitPrice,
                discount: item.discount || "",
                tax: item.taxAmount || item.tax || "",
                amount: item.totalAmount,
                unitTitle: item.unitTitle || "Box",
                level: 0,
              });
            });
          });

          return { allPackages, allServices, allItems };
        };

        // Helper function to collect items for combined table (ordered: packages -> services -> items)
        const collectForCombinedTable = () => {
          const allItems: any[] = [];

          // 1. Packages first (top level)
          packages.forEach((pkg: any) => {
            allItems.push({
              ...pkg,
              type: "package",
              description: formatDescription(pkg),
              quantity: pkg.quantity,
              unitPrice: pkg.unitPrice,
              discount: pkg.discount || "",
              tax: pkg.taxAmount || pkg.tax || "",
              amount: pkg.totalAmount,
              unitTitle: "Package(s)",
              level: 0,
            });
          });

          // 2. Services: invoiceServices + package nested services
          services.forEach((service: any) => {
            allItems.push({
              ...service,
              type: "service",
              description: formatDescription(service),
              quantity: service.quantity,
              unitPrice: service.price || service.unitPrice,
              discount: service.discount || "",
              tax: service.taxAmount || service.tax || "",
              amount: service.totalAmount,
              unitTitle: "Service(s)",
              level: 0,
            });
          });

          // Services nested in packages
          packages.forEach((pkg: any) => {
            pkg.services?.forEach((service: any) => {
              allItems.push({
                ...service,
                type: "service",
                description: formatDescription(service),
                quantity: service.quantity,
                unitPrice: service.price || service.unitPrice,
                discount: service.discount || "",
                tax: service.taxAmount || service.tax || "",
                amount: service.totalAmount,
                unitTitle: "Service(s)",
                level: 1,
                parentType: "package",
              });
            });
          });

          // 3. Items: invoiceItems + package nested items + package/service nested items + service nested items
          items.forEach((item: any) => {
            allItems.push({
              ...item,
              type: "item",
              description: formatDescription(item),
              quantity: item.quantity,
              unitPrice: item.unitPrice,
              discount: item.discount || "",
              tax: item.taxAmount || item.tax || "",
              amount: item.totalAmount,
              unitTitle: item.unitTitle || "Box",
              level: 0,
            });
          });

          // Items nested in packages
          packages.forEach((pkg: any) => {
            pkg.items?.forEach((item: any) => {
              allItems.push({
                ...item,
                type: "item",
                description: formatDescription(item),
                quantity: item.quantity,
                unitPrice: item.price || item.unitPrice,
                discount: item.discount || "",
                tax: item.taxAmount || item.tax || "",
                amount: item.totalAmount,
                unitTitle: item.unitTitle || "Box",
                level: 1,
                parentType: "package",
              });
            });
          });

          // Items nested in services within packages
          packages.forEach((pkg: any) => {
            pkg.services?.forEach((service: any) => {
              service.items?.forEach((item: any) => {
                allItems.push({
                  ...item,
                  type: "item",
                  description: formatDescription(item),
                  quantity: item.quantity,
                  unitPrice: item.price || item.unitPrice,
                  discount: item.discount || "",
                  tax: item.taxAmount || item.tax || "",
                  amount: item.totalAmount,
                  unitTitle: item.unitTitle || "Box",
                  level: 2,
                  parentType: "service",
                });
              });
            });
          });

          // Items nested in standalone services
          services.forEach((service: any) => {
            service.items?.forEach((item: any) => {
              allItems.push({
                ...item,
                type: "item",
                description: formatDescription(item),
                quantity: item.quantity,
                unitPrice: item.price || item.unitPrice,
                discount: item.discount || "",
                tax: item.taxAmount || item.tax || "",
                amount: item.totalAmount,
                unitTitle: item.unitTitle || "Box",
                level: 1,
                parentType: "service",
              });
            });
          });

          return allItems;
        };

        // Helper function to get serial number with indentation
        const getSerialNumber = (
          type: string,
          index: number,
          level: number,
        ) => {
          const prefix =
            type === "package" ? "P" : type === "service" ? "S" : "I";
          const indent = "  ".repeat(level);
          return `${indent}${prefix}${index}`;
        };

        // Helper function to render quantity with unit
        const renderQuantity = (item: any) => {
          const qty = item.quantity || 0;
          const unit =
            item.unitTitle ||
            (item.type === "service"
              ? "Service(s)"
              : item.type === "package"
                ? "Package(s)"
                : "Box");

          return (
            <>
              <Box component="strong" sx={{ color: "primary.main", mx: 0.5 }}>
                {qty}
              </Box>
              {unit}
            </>
          );
        };

        // Helper function to render currency value
        const renderCurrencyValue = (value: number) => (
          <>
            <Box component="strong" sx={{ color: "primary.main", mx: 0.5 }}>
              {formatCurrency(value)}
            </Box>
            <span>{loggedInUserCurrency?.toUpperCase() || ""}</span>
          </>
        );

        // Helper function to render table header cell
        const renderTableHeaderCell = (content: React.ReactNode) => (
          <TableCell
            sx={{
              border: "1px solid #ddd",
              padding: "12px",
              fontWeight: "bold",
              backgroundColor: "#f5f5f5",
              textAlign: "center",
            }}
          >
            {content}
          </TableCell>
        );

        // Helper function to render table body cell
        const renderTableBodyCell = (
          content: React.ReactNode,
          options: { align?: "left" | "center" | "right"; sx?: any } = {},
        ) => (
          <TableCell
            sx={{
              border: "1px solid #ddd",
              padding: "8px",
              textAlign: options.align || "left",
              ...options.sx,
            }}
          >
            {content}
          </TableCell>
        );

        // Render combined table
        const renderCombinedTable = (tableItems: any[], columnsConfig: any) => {
          const allItems = tableItems.filter((item) => {
            if (item.type === "package" && settings.showPackages === false)
              return false;
            if (item.type === "service" && settings.showServices === false)
              return false;
            if (item.type === "item" && settings.showItems === false)
              return false;
            return true;
          });

          return (
            <Box sx={{ mb: 3 }}>
              <TableContainer>
                <Table
                  sx={{
                    border: "1px solid #ddd",
                    borderCollapse: "collapse",
                    fontSize: "13px",
                  }}
                  size="small"
                >
                  <TableHead>
                    <TableRow>
                      {columnsConfig.serialNo !== false &&
                        renderTableHeaderCell(
                          renderLanguage(
                            getTableColumnLabel("serialNo").primary,
                            getTableColumnLabel("serialNo").secondary,
                          ),
                        )}
                      {columnsConfig.description !== false &&
                        renderTableHeaderCell(
                          renderLanguage(
                            getTableColumnLabel("description").primary,
                            getTableColumnLabel("description").secondary,
                          ),
                        )}
                      {columnsConfig.quantity !== false &&
                        renderTableHeaderCell(
                          renderLanguage(
                            getTableColumnLabel("quantity").primary,
                            getTableColumnLabel("quantity").secondary,
                          ),
                        )}
                      {columnsConfig.unitPrice !== false &&
                        renderTableHeaderCell(
                          renderLanguage(
                            getTableColumnLabel("unitPrice").primary,
                            getTableColumnLabel("unitPrice").secondary,
                          ),
                        )}
                      {columnsConfig.discount !== false &&
                        renderTableHeaderCell(
                          renderLanguage(
                            getTableColumnLabel("discount").primary,
                            getTableColumnLabel("discount").secondary,
                          ),
                        )}
                      {columnsConfig.tax !== false &&
                        renderTableHeaderCell(
                          renderLanguage(
                            getTableColumnLabel("tax").primary,
                            getTableColumnLabel("tax").secondary,
                          ),
                        )}
                      {columnsConfig.amount !== false &&
                        renderTableHeaderCell(
                          renderLanguage(
                            getTableColumnLabel("amount").primary,
                            getTableColumnLabel("amount").secondary,
                          ),
                        )}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {allItems.map((item, index) => {
                      const paddingLeft = (item.level || 0) * 24;

                      return (
                        <TableRow key={index}>
                          {columnsConfig.serialNo !== false &&
                            renderTableBodyCell(
                              getSerialNumber(
                                item.type,
                                index + 1,
                                item.level || 0,
                              ),
                              { align: "center" },
                            )}
                          {columnsConfig.description !== false &&
                            renderTableBodyCell(
                              <span style={{ paddingLeft: `${paddingLeft}px` }}>
                                {item.description}
                              </span>,
                            )}
                          {columnsConfig.quantity !== false &&
                            renderTableBodyCell(renderQuantity(item), {
                              align: "center",
                            })}
                          {columnsConfig.unitPrice !== false &&
                            renderTableBodyCell(
                              renderCurrencyValue(item.unitPrice),
                              { align: "right" },
                            )}
                          {columnsConfig.discount !== false &&
                            renderTableBodyCell(
                              renderCurrencyValue(item.discount),
                              { align: "right" },
                            )}
                          {columnsConfig.tax !== false &&
                            renderTableBodyCell(renderCurrencyValue(item.tax), {
                              align: "right",
                            })}
                          {columnsConfig.amount !== false &&
                            renderTableBodyCell(
                              renderCurrencyValue(item.amount),
                              { align: "right" },
                            )}
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          );
        };

        // Render separate table
        const renderSeparateTable = (
          titlePrimary: string,
          titleSecondary: string,
          tableItems: any[],
          columnsConfig: any,
        ) => {
          if (tableItems.length === 0) return null;

          return (
            <Box sx={{ mb: 2 }}>
              <Typography variant="h6" sx={{ mb: 1, fontWeight: "bold" }}>
                {renderLanguage(titlePrimary, titleSecondary)}
              </Typography>
              <TableContainer>
                <Table
                  sx={{
                    border: "1px solid #ddd",
                    borderCollapse: "collapse",
                    fontSize: "13px",
                  }}
                  size="small"
                >
                  <TableHead>
                    <TableRow>
                      {columnsConfig.serialNo !== false &&
                        renderTableHeaderCell(
                          renderLanguage(
                            getTableColumnLabel("serialNo").primary,
                            getTableColumnLabel("serialNo").secondary,
                          ),
                        )}
                      {columnsConfig.description !== false &&
                        renderTableHeaderCell(
                          renderLanguage(
                            getTableColumnLabel("description").primary,
                            getTableColumnLabel("description").secondary,
                          ),
                        )}
                      {columnsConfig.quantity !== false &&
                        renderTableHeaderCell(
                          renderLanguage(
                            getTableColumnLabel("quantity").primary,
                            getTableColumnLabel("quantity").secondary,
                          ),
                        )}
                      {columnsConfig.unitPrice !== false &&
                        renderTableHeaderCell(
                          renderLanguage(
                            getTableColumnLabel("unitPrice").primary,
                            getTableColumnLabel("unitPrice").secondary,
                          ),
                        )}
                      {columnsConfig.discount !== false &&
                        renderTableHeaderCell(
                          renderLanguage(
                            getTableColumnLabel("discount").primary,
                            getTableColumnLabel("discount").secondary,
                          ),
                        )}
                      {columnsConfig.tax !== false &&
                        renderTableHeaderCell(
                          renderLanguage(
                            getTableColumnLabel("tax").primary,
                            getTableColumnLabel("tax").secondary,
                          ),
                        )}
                      {columnsConfig.amount !== false &&
                        renderTableHeaderCell(
                          renderLanguage(
                            getTableColumnLabel("amount").primary,
                            getTableColumnLabel("amount").secondary,
                          ),
                        )}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {tableItems.map((item, index) => {
                      const paddingLeft = (item.level || 0) * 24;

                      return (
                        <TableRow key={index}>
                          {columnsConfig.serialNo !== false &&
                            renderTableBodyCell(
                              getSerialNumber(
                                item.type,
                                index + 1,
                                item.level || 0,
                              ),
                              { align: "center" },
                            )}
                          {columnsConfig.description !== false &&
                            renderTableBodyCell(
                              <span style={{ paddingLeft: `${paddingLeft}px` }}>
                                {item.description}
                              </span>,
                            )}
                          {columnsConfig.quantity !== false &&
                            renderTableBodyCell(renderQuantity(item), {
                              align: "center",
                            })}
                          {columnsConfig.unitPrice !== false &&
                            renderTableBodyCell(
                              renderCurrencyValue(item.unitPrice),
                              { align: "right" },
                            )}
                          {columnsConfig.discount !== false &&
                            renderTableBodyCell(
                              item?.discount &&
                                renderCurrencyValue(item.discount),
                              { align: "right" },
                            )}
                          {columnsConfig.tax !== false &&
                            renderTableBodyCell(
                              item?.tax || item?.taxAmount
                                ? renderCurrencyValue(item.tax)
                                : "",
                              { align: "right" },
                            )}
                          {columnsConfig.amount !== false &&
                            renderTableBodyCell(
                              renderCurrencyValue(item.amount),
                              { align: "right" },
                            )}
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          );
        };

        // Main rendering logic based on tableLayout
        if (tableLayout === "separate") {
          const { allPackages, allServices, allItems } =
            collectForSeparateTables();

          const filteredPackages =
            settings.showPackages !== false ? allPackages : [];
          const filteredServices =
            settings.showServices !== false ? allServices : [];
          const filteredItems = settings.showItems !== false ? allItems : [];

          return (
            <Box sx={{ mb: 3 }}>
              {filteredPackages.length > 0 &&
                renderSeparateTable(
                  getTableSectionLabel("packages").primary,
                  getTableSectionLabel("packages").secondary,
                  filteredPackages,
                  tableColumns,
                )}
              {filteredServices.length > 0 &&
                renderSeparateTable(
                  getTableSectionLabel("services").primary,
                  getTableSectionLabel("services").secondary,
                  filteredServices,
                  tableColumns,
                )}
              {filteredItems.length > 0 &&
                renderSeparateTable(
                  getTableSectionLabel("items").primary,
                  getTableSectionLabel("items").secondary,
                  filteredItems,
                  tableColumns,
                )}
            </Box>
          );
        } else {
          // Combined table layout
          const allItems = collectForCombinedTable();
          return renderCombinedTable(allItems, tableColumns);
        }
      }

      case "summary": {
        const summaryItemsConfig = settings.summaryItems || {};
        const paidAmount =
          (invoice.totalAmount || 0) - (invoice.balanceAmount || 0);

        const summaryData: Array<{
          key: string;
          label: string;
          value: string;
          isTotal?: boolean;
        }> = [];

        if (summaryItemsConfig.subtotal !== false) {
          const labels = getSummaryLabel("subtotal");
          summaryData.push({
            key: "subtotal",
            label: renderLanguage(labels.primary, labels.secondary) as any,
            value: `${formatCurrency(invoice.subtotalAmount)} `,
          });
        }

        if (summaryItemsConfig.discount !== false) {
          const labels = getSummaryLabel("discount");
          summaryData.push({
            key: "discount",
            label: renderLanguage(labels.primary, labels.secondary) as any,
            value: `${formatCurrency(invoice.discount)} `,
          });
        }
        if (summaryItemsConfig.discountedSubtotal !== false) {
          const labels = getSummaryLabel("discountedSubtotal");
          summaryData.push({
            key: "discountedSubtotal",
            label: renderLanguage(labels.primary, "المجموع بعد الخصم") as any,
            value: `${formatCurrency(invoice.subtotalAmount - invoice.discount)} `,
          });
        }

        if (summaryItemsConfig.tax !== false) {
          const labels = getSummaryLabel("tax");
          summaryData.push({
            key: "tax",
            label: renderLanguage(labels.primary, labels.secondary) as any,
            value: `${formatCurrency(invoice.taxAmount)} `,
          });
        }

        if (summaryItemsConfig.total !== false) {
          const labels = getSummaryLabel("total");
          summaryData.push({
            key: "total",
            label: renderLanguage(labels.primary, labels.secondary) as any,
            value: `${formatCurrency(invoice.totalAmount)} `,
            isTotal: true,
          });
        }

        // Payment info items (always show if showPaymentInfo is true)
        if (settings.showPaymentInfo) {
          const creditLabels = getSummaryLabel("creditAmount");
          summaryData.push({
            key: "creditAmount",
            label: renderLanguage(
              creditLabels.primary,
              creditLabels.secondary,
            ) as any,
            value: `${formatCurrency(invoice.attributes?.creditAmount || 0)} `,
          });

          const paidLabels = getSummaryLabel("paidAmount");
          summaryData.push({
            key: "paidAmount",
            label: renderLanguage(
              paidLabels.primary,
              paidLabels.secondary,
            ) as any,
            value: `${formatCurrency(paidAmount)} `,
          });

          const dueLabels = getSummaryLabel("dueAmount");
          summaryData.push({
            key: "dueAmount",
            label: renderLanguage(
              dueLabels.primary,
              dueLabels.secondary,
            ) as any,
            value: `${formatCurrency(invoice.balanceAmount)} `,
          });
        }
        return (
          <Box
            key="summary"
            sx={{
              mb: 3,
              display: "flex",
              justifyContent: "flex-end",
              flexDirection: "column",
            }}
          >
            {settings.showPaymentInfo && invoice?.paymentMode && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" className="text-end">
                  <strong>
                    {renderLanguage(
                      getPaymentLabel().primary,
                      getPaymentLabel().secondary,
                    )}
                    :
                  </strong>{" "}
                  {invoice?.paymentMode || ""}
                </Typography>
              </Box>
            )}
            <Box className="flex gap-2 justify-between w-full">
              <Box className="text-start">
                <strong>{t("Signature")} :</strong>
                <Box className="border border-solid rounded-[8px] h-[100px] p-[10px] min-w-[200px]">
                  {invoice?.signature && (
                    <img
                      src={invoice.signature}
                      alt="signature"
                      style={{
                        maxHeight: "100%",
                        maxWidth: "100%",
                        objectFit: "contain",
                      }}
                    />
                  )}
                </Box>
              </Box>
              <table
                style={{
                  minWidth: 300,
                  borderCollapse: "collapse",
                  border: "1px solid #ddd",
                  fontSize: "13px",
                }}
              >
                <tbody>
                  {summaryData.map((item) => (
                    <tr
                      key={item.key}
                      style={{
                        borderBottom: item.isTotal
                          ? "5px solid" + themeColors.secondary
                          : undefined,
                        fontWeight: item.isTotal ? "bold" : undefined,
                      }}
                    >
                      <td style={{ border: "1px solid #ddd", padding: "8px" }}>
                        {item.label}
                      </td>
                      <td
                        style={{
                          border: "1px solid #ddd",
                          padding: "8px",
                          textAlign: "right",
                        }}
                      >
                        <span style={{ fontWeight: "bold", margin: "0px 2px" }}>
                          {item.value}
                        </span>{" "}
                        {loggedInUserCurrency?.toUpperCase() || ""}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Box>
            {invoice?.paymentDetail && invoice.paymentDetail.length > 0 && (
              <table
                border={1}
                className="border-collapse border border-solid block ms-auto mb-[20px] max-w-[80%] mt-2 text-[13px]"
              >
                <thead>
                  <tr className="bg-gray-50">
                    <th className="p-3 font-bold border border-solid text-center">
                      {renderLanguage(
                        getTranslation("paymentNo", "primary"),
                        getTranslation("paymentNo", "secondary"),
                      )}{" "}
                    </th>
                    <th className="p-3 font-bold border border-solid text-center">
                      {renderLanguage(
                        getTranslation("paymentMode", "primary"),
                        getTranslation("paymentMode", "secondary"),
                      )}{" "}
                    </th>
                    <th className="p-3 font-bold border border-solid text-center">
                      {renderLanguage(
                        getTranslation("paymentDate", "primary"),
                        getTranslation("paymentDate", "secondary"),
                      )}{" "}
                    </th>
                    <th className="p-3 font-bold border border-solid text-center">
                      {renderLanguage(
                        getTranslation("amount", "primary"),
                        getTranslation("amount", "secondary"),
                      )}{" "}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {(invoice?.paymentDetail || [])?.map(
                    (payment: any, key: number) => {
                      return (
                        <tr key={key}>
                          <td className="p-1 border border-solid text-center">
                            {payment.typeNumber}
                          </td>
                          <td className="p-1 border border-solid text-center">
                            {payment.mode}
                          </td>
                          <td className="p-1 border border-solid text-center">
                            {payment.date}
                          </td>
                          <td className="p-1 border border-solid text-center">
                            <span className="font-bold">
                              {formatNumber(payment.amount)}
                            </span>
                            <span className="mx-1">
                              {loggedInUserCurrency?.toUpperCase() || ""}
                            </span>
                          </td>
                        </tr>
                      );
                    },
                  )}
                </tbody>
              </table>
            )}
            {settings.showPaymentInfo && invoice.attributes?.paymentDetail && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2">
                  {invoice?.attributes?.paymentDetail || ""}
                </Typography>
              </Box>
            )}
          </Box>
        );
      }

      case "footer": {
        if (!settings.showFooter) return null;

        const footerItems = settings?.footerItems || {};
        const leftItems = [];
        const brandingFooterText =
          branding?.attributes?.footerText?.trim() || "";
        const brandingAboutHref = getBrandingAboutHref(
          branding?.attributes?.about,
        );

        if (footerItems.address && invoice.attributes?.stationAddress) {
          leftItems.push(invoice.attributes.stationAddress);
        }
        if (footerItems.email && invoice.attributes?.stationEmail) {
          leftItems.push(invoice.attributes.stationEmail);
        }
        if (footerItems.phone && invoice.attributes?.stationPhone) {
          leftItems.push(invoice.attributes.stationPhone);
        }

        return (
          <Box key="footer" sx={{ mt: 3, pt: 2, borderTop: "1px solid #ddd" }}>
            {settings.footerText && (
              <Typography
                variant="caption"
                align="center"
                display="block"
                sx={{ mb: settings.footerText ? 2 : 0 }}
              >
                {t(settings?.footerText || "")}
              </Typography>
            )}
            <Box className="flex gap-2 justify-between">
              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 1.5,
                  alignItems: "center",
                  flex: 1,
                }}
              >
                <Typography
                  variant="caption"
                  sx={{ fontSize: "0.75rem", color: "#666" }}
                >
                  {leftItems.join(" | ")}
                </Typography>
              </Box>
              {/* {footerItems?.pageNumber && (
                                <Typography variant="caption" sx={{ fontSize: "0.75rem", color: "#666" }}>
                                    {(() => {
                                        const label = getFooterLabel("pageNumber");
                                        return `${renderLabel(label.primary, label.secondary)} 1`;
                                    })()}
                                </Typography>
                            )} */}
            </Box>
            {brandingFooterText ? (
              <Typography
                variant="caption"
                align="center"
                display="block"
                className="mt-[15px]"
              >
                {brandingAboutHref ? (
                  <a
                    href={brandingAboutHref}
                    target="_blank"
                    rel="noreferrer"
                    style={{ color: themeColors?.primary }}
                  >
                    {brandingFooterText}
                  </a>
                ) : (
                  brandingFooterText
                )}
              </Typography>
            ) : null}
          </Box>
        );
      }

      case "terms": {
        if (settings.showTerms === false) return null;
        const primaryTerms = invoice.attributes?.stationTermAndCondition;
        const secondaryTerms =
          invoice.attributes?.stationTermAndConditionSecondary;
        const languageDisplay = settings.languageDisplay || "both";
        const isSecondaryArabicOrUrdu = ["ar", "ur"].includes(
          secondaryLanguage.toLowerCase(),
        );

        if (!primaryTerms && !secondaryTerms) return null;

        if (languageDisplay === "both" && primaryTerms && secondaryTerms) {
          return (
            <Box key="terms" sx={{ mt: 2 }}>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 2,
                }}
              >
                <Box className="flex gap-2 justify-between">
                  {languageDisplay !== "secondary" && (
                    <Typography variant="subtitle2" sx={{ fontWeight: "bold" }}>
                      Terms & Conditions
                    </Typography>
                  )}
                  {languageDisplay !== "primary" && (
                    <Typography variant="subtitle2" sx={{ fontWeight: "bold" }}>
                      الأحكام والشروط
                    </Typography>
                  )}
                </Box>
                <Box className="flex gap-6" sx={{ wordBreak: "break-all" }}>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body2" className="text-start">
                      {primaryTerms}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      flex: 1,
                      textAlign:
                        settings.languageDisplay === "secondary"
                          ? "left"
                          : "right",
                    }}
                  >
                    <Typography
                      variant="body2"
                      className={
                        secondaryLanguage === "arabic" ||
                        secondaryLanguage === "urdu"
                          ? "ff-arabic"
                          : ""
                      }
                    >
                      {secondaryTerms}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Box>
          );
        } else if (languageDisplay === "secondary" && secondaryTerms) {
          return (
            <Box key="terms" sx={{ mt: 2 }}>
              <Box
                sx={{
                  display: "flex",
                  gap: 2,
                  flexDirection: "column",
                }}
              >
                <Typography
                  variant="subtitle2"
                  sx={{ fontWeight: "bold", textAlign: "end" }}
                >
                  الأحكام والشروط
                </Typography>
                <Typography
                  variant="body2"
                  className={isSecondaryArabicOrUrdu ? "ff-arabic" : ""}
                >
                  {secondaryTerms}
                </Typography>
              </Box>
            </Box>
          );
        } else if (primaryTerms) {
          return (
            <Box key="terms" sx={{ mt: 2 }}>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 2,
                }}
              >
                <Typography
                  variant="subtitle2"
                  sx={{ fontWeight: "bold", textAlign: "start" }}
                  className={
                    secondaryLanguage === "arabic" ||
                    secondaryLanguage === "urdu"
                      ? "ff-arabic"
                      : ""
                  }
                >
                  Terms & Conditions
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ textAlign: "start" }}
                  className={
                    secondaryLanguage === "arabic" ||
                    secondaryLanguage === "urdu"
                      ? "ff-arabic"
                      : ""
                  }
                >
                  {primaryTerms}
                </Typography>
              </Box>
            </Box>
          );
        }
        return null;
      }

      case "notes": {
        if (!settings.showNotes) return null;
        const notes = invoice.notes || invoice.attributes?.notes;
        if (!notes) return null;

        const notesLabels = {
          primary: getTranslation("notes", "primary"),
          secondary: getTranslation("الملاحظات", "secondary"),
        };
        return (
          <Box key="notes" sx={{ mt: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: "bold" }}>
              {renderLanguage(notesLabels.primary, notesLabels.secondary)}:
            </Typography>
            <Typography variant="body2">{notes}</Typography>
          </Box>
        );
      }

      default:
        return null;
    }
  };

  const sectionOrder = settings?.sectionOrder || [
    "logo",
    "header",
    "vehicleInfo",
    "table",
    "summary",
    "terms",
    "notes",
    "footer",
  ];
  const DownloadPDF = async () => {
    setDownloading(true);
    try {
      const element: any = invoiceRef.current;

      // Ensure element has dimensions
      if (element.scrollWidth === 0 || element.scrollHeight === 0) {
        enqueueSnackbar(<AlertMessages text={"Not Found"} />, {
          variant: "error",
        });
        setDownloading(false);
        return;
      }

      // Convert all images to base64 to avoid CORS issues with html2canvas
      const images = element.querySelectorAll("img");
      const imageDataMap = new Map<HTMLImageElement, string>();

      const convertImageToBase64 = async (img: any): Promise<void> => {
        // If already a data URL, skip
        if (img.src.startsWith("data:")) return;

        try {
          let blob: Blob;
          const originalUrl = img.src;

          try {
            const { APP_BASE_URL } = await import(
              "subModule/src/constant/Config"
            );
            const proxyUrl = `${APP_BASE_URL}/api/proxy-image?url=${
              originalUrl
            }`;

            const response = await axiosClient.get(proxyUrl, {
              responseType: "blob",
              timeout: 10000,
            });

            blob = response.data as Blob;
          } catch (proxyError) {
            // If proxy fails, try direct fetch with CORS
            console.warn(
              "Proxy fetch failed, trying direct CORS fetch:",
              proxyError,
            );
            try {
              const separator = originalUrl.includes("?") ? "&" : "?";
              const url = `${originalUrl}${separator}t=${new Date().getTime()}`;
              const response = await fetch(url, {
                cache: "no-store",
                mode: "cors",
                credentials: "omit",
              });

              if (!response.ok) throw new Error(`HTTP ${response.status}`);
              blob = await response.blob();
            } catch (corsError) {
              // Last resort: use a public CORS proxy (not ideal for production)
              console.warn(
                "Direct CORS fetch failed, using CORS proxy:",
                corsError,
              );
              const corsProxyUrl = `https://corsproxy.io/${originalUrl}`;
              const response = await fetch(corsProxyUrl, {
                cache: "no-store",
                mode: "cors",
              });

              if (!response.ok) throw new Error(`HTTP ${response.status}`);
              blob = await response.blob();
            }
          }

          await new Promise<void>((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => {
              if (reader.result && typeof reader.result === "string") {
                imageDataMap.set(img, img.src); // Store original src
                img.src = reader.result; // Replace with base64
              }
              resolve();
            };
            reader.onerror = () => reject(new Error("FileReader failed"));
            reader.readAsDataURL(blob);
          });
        } catch (err) {
          console.warn("Failed to convert image to base64:", img.src, err);
          // If all methods fail, the image will remain as-is
          // html2canvas might still capture it if allowTaint is true
        }
      };

      // Convert all images to base64
      await Promise.all(Array.from(images).map(convertImageToBase64));

      // Wait a bit for images to render with new src
      await new Promise((resolve) => setTimeout(resolve, 500));

      // Wait for next frame to ensure rendering is complete
      await new Promise<void>((resolve) =>
        requestAnimationFrame(() => resolve()),
      );
      await new Promise<void>((resolve) =>
        requestAnimationFrame(() => resolve()),
      );

      // Wait for content to be rendered - check if InvoiceRenderer has rendered
      let retries = 0;
      const maxRetries = 10;
      while (retries < maxRetries) {
        const hasContent =
          element.querySelector(".invoice-renderer") ||
          element.textContent?.trim().length > 0 ||
          element.scrollHeight > 100;

        if (hasContent && element.scrollWidth > 0 && element.scrollHeight > 0) {
          break;
        }

        await new Promise((resolve) => setTimeout(resolve, 200));
        retries++;
      }

      // Final check - element must have dimensions
      if (element.scrollWidth === 0 || element.scrollHeight === 0) {
        console.error("Element dimensions:", {
          width: element.scrollWidth,
          height: element.scrollHeight,
          hasContent: element.textContent?.trim().length,
        });
        throw new Error("Element has no dimensions or content");
      }

      // Ensure element is visible and in viewport
      window.scrollTo(0, 0); // Reset scroll to top for html2canvas
      element.scrollIntoView({ behavior: "instant", block: "start" });
      await new Promise((resolve) => setTimeout(resolve, 100));

      const options = {
        scale: 2,
        useCORS: true, // Try CORS first
        allowTaint: false, // Don't allow tainted canvas (we want clean data)
        logging: false,
        backgroundColor: "#ffffff",
        // width: element.scrollWidth,
        // windowHeight: element.scrollHeight,
        scrollY: 0,
      };
      await document.fonts.ready;
      element.classList.add("pdf-fix-arabic");

      const originalMaxWidth = element.style.maxWidth;
      const originalWidth = element.style.width;

      const pdfWidthMm = isThermalPrint(settings)
        ? getThermalPaperWidthMm(settings)
        : 210;

      element.style.maxWidth = "none";
      element.style.width = `${pdfWidthMm}mm`;
      const canvas = await html2canvas(element, options);

      // Restore original image sources
      imageDataMap.forEach((originalSrc, img) => {
        img.src = originalSrc;
      });
      element.style.maxWidth = originalMaxWidth;
      element.style.width = originalWidth;
      element.classList.remove("pdf-fix-arabic");
      // Verify canvas has content
      if (canvas.width === 0 || canvas.height === 0) {
        throw new Error("Canvas is empty");
      }

      const imgData = canvas.toDataURL("image/png");

      // Verify image data is valid
      if (!imgData || imgData === "data:,") {
        throw new Error("Failed to generate image data");
      }

      const pageWidth = pdfWidthMm;
      const pageHeight = isThermalPrint(settings) ? 297 : 297;
      const pdf = isThermalPrint(settings)
        ? new jsPDF("p", "mm", [pageWidth, pageHeight])
        : new jsPDF("p", "mm", "a4");

      const ratio = pageWidth / canvas.width;
      const pageCanvasHeight = Math.floor((pageHeight - 10) / ratio);

      let position = 0;

      while (position < canvas.height) {
        const pageCanvas = document.createElement("canvas");
        pageCanvas.width = canvas.width;
        pageCanvas.height = Math.min(
          pageCanvasHeight,
          canvas.height - position,
        );

        const ctx = pageCanvas.getContext("2d")!;
        ctx.drawImage(
          canvas,
          0,
          position,
          canvas.width,
          pageCanvas.height,
          0,
          0,
          canvas.width,
          pageCanvas.height,
        );

        if (position > 0) pdf.addPage();

        pdf.addImage(
          pageCanvas.toDataURL("image/png"),
          "PNG",
          0,
          0,
          pageWidth,
          (pageCanvas.height * pageWidth) / canvas.width,
        );

        position += pageCanvasHeight - 20;
      }

      const fileLabel = "invoice";
      pdf.save(`${fileLabel}-${invoice?.invoiceNo || "document"}.pdf`);
      setDownloading(false);
    } catch (error) {
      console.error("Error generating PDF:", error);
      enqueueSnackbar(<AlertMessages text={"failedToGeneratePDF"} />, {
        variant: "error",
      });
      setDownloading(false);
    }
  };
  const thermalWidthMm = isThermalPrint(settings)
    ? getThermalPaperWidthMm(settings)
    : null;
  const thermalData =
    thermalWidthMm != null ? mapInvoiceToThermalData(invoice, settings) : null;
  const isModernDesign =
    !thermalData && (settings?.designVariant || "classic") === "modern";
  const modernData = isModernDesign
    ? mapInvoiceToModernData(invoice, settings, {
        t,
        logoUrl: stationLogoBase64Src || undefined,
      })
    : null;

  return (
    <>
      {isRefetching || invoiceRefetch || loading ? (
        <Loader />
      ) : (
        <>
          <Box
            className="flex justify-between items-center hide-on-print bg-[#fff] rounded-[8px] mb-[15px]"
            sx={{
              p: 2,
              maxWidth: thermalWidthMm ? `${thermalWidthMm}mm` : "1000px",
              margin: "0 auto",
            }}
            id="form"
          >
            <Button
              className="secondary"
              variant="contained"
              style={{ color: themeColors?.primary }}
              disableElevation
              onClick={() => navigate(-1)}
            >
              {t("back")}
            </Button>
            <Box className="flex gap-2">
              <Button
                className="primary"
                variant="contained"
                style={{ background: themeColors?.primary }}
                disableElevation
                onClick={() => window.print()}
              >
                {t("Print")}
              </Button>
              <Button
                className="primary"
                variant="contained"
                style={{
                  display: "none",
                  background: "transparent",
                  color: themeColors?.secondary,
                  border: "2px solid" + themeColors?.secondary,
                }}
                disableElevation
                onClick={() => DownloadPDF()}
              >
                {downloading ? (
                  <CircularProgress
                    sx={{ color: themeColors?.primary, marginRight: "8px" }}
                    size={16}
                  />
                ) : (
                  t("download")
                )}
              </Button>
            </Box>
          </Box>
          <Box
            sx={{
              p: isModernDesign ? 0 : 2,
              maxWidth: thermalWidthMm ? `${thermalWidthMm}mm` : "1000px",
              margin: "0 auto",
              backgroundColor: isModernDesign ? "transparent" : "#fff",
              borderRadius: "8px",
              fontFamily: "Saira, sans-serif",
              "@media print": thermalWidthMm
                ? {
                    maxWidth: `${thermalWidthMm}mm`,
                    width: `${thermalWidthMm}mm`,
                    margin: 0,
                    p: 0,
                  }
                : {},
            }}
            id="print-root"
            ref={invoiceRef}
          >
            {thermalData ? (
              <ThermalInvoiceContent
                settings={settings}
                data={thermalData}
                theme={themeColors}
                formatNumber={(n) => formatNumber(n)}
                currencySymbol={
                  loggedInUserCurrency
                    ? `${loggedInUserCurrency.toUpperCase()} `
                    : ""
                }
              />
            ) : modernData ? (
              <ModernInvoicePreview
                settings={settings}
                theme={themeColors}
                templateType={2}
                data={modernData}
                logoSrc={stationLogoBase64Src || modernData.logoUrl}
                currencySymbol={
                  loggedInUserCurrency
                    ? loggedInUserCurrency.toUpperCase()
                    : "AED"
                }
              />
            ) : (
              <Box>
                {sectionOrder.map((sectionId: any) => renderSection(sectionId))}
              </Box>
            )}
          </Box>
        </>
      )}
    </>
  );
};

export default InvoiceDownloadPreview;
